import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Pagination,
  Select,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import {
  HiOutlineOfficeBuilding,
  HiOutlineSearch,
  HiOutlineAdjustments,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};

const formatInterval = (months) => {
  const raw = Number(months);
  if (!Number.isFinite(raw) || raw <= 0) return "-";
  if (raw === 1) return "Mensal";
  if (raw === 3) return "Trimestral";
  if (raw === 12) return "Anual";
  return `${raw} meses`;
};

const formatExpiration = (date) => {
  if (!date) return "—";
  const parsed = moment(date);
  if (!parsed.isValid()) return "—";
  return parsed.format("DD/MM/YYYY");
};

const TRIAL_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "trial", label: "Em teste" },
  { value: "paid", label: "Contratado" },
];

const PER_PAGE_OPTIONS = [10, 20, 50];

export default function CompaniesOverviewPage() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [trialFilter, setTrialFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, planFilter, trialFilter, itemsPerPage]);

  const {
    data: overview,
    isLoading,
    error,
  } = useQuery(
    [
      "companies-overview",
      searchQuery,
      planFilter,
      trialFilter,
      currentPage,
      itemsPerPage,
    ],
    async () => {
      const params = {
        page: currentPage,
        itemsPerPage,
      };
      if (searchQuery) params.search = searchQuery;
      if (planFilter) params.planId = planFilter;
      if (trialFilter !== "all") params.trialStatus = trialFilter;

      const response = await axios.get(ADMIN.GET_COMPANIES_OVERVIEW, {
        params,
      });
      return response.data?.data || null;
    },
    { keepPreviousData: true, staleTime: 5 * 60 * 1000 }
  );

  const totals = overview?.totals ?? {
    companies: 0,
    funnels: 0,
    teams: 0,
    users: 0,
    filiais: 0,
  };
  const companies = overview?.companies ?? [];
  const plans = overview?.plans ?? [];
  const pagination = overview?.pagination ?? {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage,
  };

  const planOptions = useMemo(
    () =>
      plans
        .map((plan) => ({
          value: String(plan.id),
          label: plan.name || plan.code || `Plano ${plan.id}`,
        }))
        .filter((plan) => plan.value),
    [plans]
  );
  const planLabelMap = useMemo(
    () =>
      planOptions.reduce((acc, plan) => {
        acc[plan.value] = plan.label;
        return acc;
      }, {}),
    [planOptions]
  );
  const activeSearch = searchInput.trim();
  const hasActiveFilters =
    Boolean(activeSearch) || Boolean(planFilter) || trialFilter !== "all";

  const resetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setPlanFilter("");
    setTrialFilter("all");
  };

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100 text-orange-800">
            <HiOutlineOfficeBuilding className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
              Empresas Relinq
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">Clientes</h1>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2 text-slate-700">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-900 text-white">
              <HiOutlineAdjustments className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">
                Filtros
              </p>
              <p className="text-sm font-semibold">
                Refine a lista de empresas
              </p>
            </div>
          </div>
          <Button
            size="xs"
            color="gray"
            disabled={!hasActiveFilters}
            onClick={resetFilters}
          >
            Limpar filtros
          </Button>
        </div>
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Busca
            </label>
            <TextInput
              placeholder="Cliente, e-mail ou documento"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              type="search"
              icon={HiOutlineSearch}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Plano
            </label>
            <Select
              value={planFilter}
              onChange={(event) => setPlanFilter(event.target.value)}
            >
              <option value="">Todos os planos</option>
              {planOptions.map((plan) => (
                <option key={plan.value} value={plan.value}>
                  {plan.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Status
            </label>
            <Select
              value={trialFilter}
              onChange={(event) => setTrialFilter(event.target.value)}
            >
              {TRIAL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Paginação
            </label>
            <Select
              value={String(itemsPerPage)}
              onChange={(event) => setItemsPerPage(Number(event.target.value))}
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value} por página
                </option>
              ))}
            </Select>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
            <span className="uppercase tracking-[0.3em] text-slate-400">
              Ativos
            </span>
            {activeSearch && <Badge color="gray">Busca: {activeSearch}</Badge>}
            {planFilter && (
              <Badge color="gray">
                Plano: {planLabelMap[planFilter] || planFilter}
              </Badge>
            )}
            {trialFilter !== "all" && (
              <Badge color="gray">
                Status:{" "}
                {TRIAL_OPTIONS.find((option) => option.value === trialFilter)
                  ?.label || trialFilter}
              </Badge>
            )}
          </div>
        )}
        {overview && (
          <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
            Mostrando {companies.length} de {pagination.totalCount} clientes
            encontrados
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Spinner color="info" />
            Carregando informações das empresas...
          </div>
        ) : error ? (
          <Alert color="failure">
            <span className="font-semibold">
              Não foi possível carregar os dados.
            </span>{" "}
            Tente novamente mais tarde.
          </Alert>
        ) : companies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50 p-6 text-center text-sm text-orange-600">
            Nenhuma empresa encontrada com os filtros selecionados.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[1080px]">
                <Table.Head className="bg-slate-50 text-slate-500">
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.35em]">
                    Empresa
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.35em]">
                    Plano
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.35em]">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.35em]">
                    Expiração
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center text-[11px] uppercase tracking-[0.35em]">
                    Métricas
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center text-[11px] uppercase tracking-[0.35em]">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y divide-slate-100">
                  {companies.map((company) => {
                    const label =
                      company.socialReason ||
                      company.name ||
                      `Empresa ${company.id}`;
                    const doc = company.document || company.email;
                    const planName =
                      company.plan?.name ||
                      company.plan?.code ||
                      "Plano não informado";
                    const cycleLabel =
                      company.planCycle?.description ||
                      formatInterval(company.planCycle?.intervalMonths);
                    const planCycleInfo =
                      cycleLabel === "-"
                        ? `${formatCurrency(company.planCycle?.price)}`
                        : `${cycleLabel} · ${formatCurrency(
                            company.planCycle?.price
                          )}`;
                    const expirationDate = company.planExpirationDate
                      ? formatExpiration(company.planExpirationDate)
                      : "Sem data";
                    const isExpired =
                      company.planExpirationDate &&
                      moment().isAfter(company.planExpirationDate, "day");
                    const badgeColor = company.isTrial ? "warning" : "success";
                    const badgeLabel = company.isTrial ? "Teste" : "Contratado";

                    return (
                      <Table.Row
                        key={company.id}
                        className="bg-white hover:bg-slate-50"
                      >
                        <Table.Cell className="pr-3 py-2">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                {label}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {doc || "Sem contato"}
                              </p>
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="flex flex-col gap-1">
                            <p className="text-sm font-semibold text-slate-900">
                              {planName}
                            </p>
                            <p className="text-xs text-gray-500 -mt-1">
                              {planCycleInfo}
                            </p>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="flex flex-col gap-2">
                            <Badge color={badgeColor} className="w-fit">
                              {badgeLabel}
                            </Badge>
                            <span className="text-[11px] text-slate-500 -mt-2">
                              {company.isTrial ? "Teste ativo" : "Contrato pago"}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`text-sm font-semibold ${
                                isExpired ? "text-rose-600" : "text-slate-900"
                              }`}
                            >
                              {expirationDate}
                            </span>
                            <span
                              className={`text-[11px] -mt-2 ${
                                isExpired ? "text-rose-600" : "text-gray-500"
                              }`}
                            >
                              {isExpired ? "Plano expirado" : "Próxima renovação"}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-2">
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-1 py-0">
                              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Funis
                              </p>
                              <p className="text-sm font-semibold text-slate-900">
                                {company.counts?.funnels ?? 0}
                              </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-1 py-0">
                              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Filiais
                              </p>
                              <p className="text-sm font-semibold text-slate-900">
                                {company.counts?.filiais ?? 0}
                              </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-1 py-0">
                              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Times
                              </p>
                              <p className="text-sm font-semibold text-slate-900">
                                {company.counts?.teams ?? 0}
                              </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-1 py-0">
                              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                Usuários
                              </p>
                              <p className="text-sm font-semibold text-slate-900">
                                {company.counts?.users ?? 0}
                              </p>
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="py-5 text-center">
                          <Link
                            to={`/companies/${company.id}`}
                            className="inline-flex"
                          >
                            <Button color="gray" size="xs" pill>
                              <HiOutlineArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 bg-gray-50 py-2">
              <p className="text-sm text-gray-500">
                Página {pagination.currentPage} de {pagination.totalPages} ·{" "}
                {pagination.totalCount} clientes filtrados
              </p>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(page) => setCurrentPage(page)}
                showIcons
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
